import { z } from "zod";
import { Json, Timestamps } from "../common/index.ts";
import {
  DocumentId,
  DocumentTypeId,
  EntityId,
  StateId,
  CustomerId,
  TransitionId,
} from "../fsm/fsm.schema.ts";
import { FsmTranslationsSchema } from "../fsm/fsm.schema.ts";
import { StateDto } from "../fsm/states/states.schema.ts";
import { BikeId } from "../bikes/bikes.schema.ts";
import { LocationId } from "../locations/locations.schema.ts";

const brandedId = <T extends string>(_brand: T) => z.string().uuid().brand<T>();

export const RequestId = brandedId("RequestId");
export type RequestId = z.infer<typeof RequestId>;

/**
 * @description Priority levels for requests
 */
export const RequestPriority = z.enum(["low", "medium", "high", "critical"]);
export type RequestPriority = z.infer<typeof RequestPriority>;

/**
 * @description A Request represents a core business request (e.g., material request, support ticket)
 * that is tracked through an FSM workflow. Every request has an associated FSM document.
 */
export const RequestDto = z
  .object({
    id: RequestId,
    customerId: CustomerId,
    fsmDocumentId: DocumentId,
    documentTypeId: DocumentTypeId,
    referenceNo: z.string().min(1),
    payload: Json,
    priority: RequestPriority.nullable(),
    createdBy: EntityId.nullable(),
    locationId: LocationId.nullable(),
    bikeId: BikeId.nullable(),
    parentId: RequestId.nullable(),
    isActive: z.boolean().default(true),
    deactivatedAt: z.string().nullable().optional(),
    deactivatedBy: z.string().uuid().nullable().optional(),
  })
  .merge(Timestamps);

export type RequestDto = z.infer<typeof RequestDto>;

/**
 * @description A single row from core_request_bikes (multi-bike material requests)
 */
export const RequestBikeDto = z.object({
  requestId: RequestId,
  bikeId: BikeId,
});
export type RequestBikeDto = z.infer<typeof RequestBikeDto>;

/**
 * @description A Request with additional information for UI display
 */
export const RequestHydrated = RequestDto.extend({
  documentTypeName: z.string(),
  // BEL-488: stable document-type key ('MAT' | 'REP' | 'WAR' | ...) — the
  // reliable discriminator for admin_edit editor variant (material-lines vs
  // single-bike), independent of the operator-configurable display name.
  documentTypeKey: z.string().nullable().optional(),
  documentTypeTranslations: FsmTranslationsSchema.nullable(),
  currentState: StateDto,
  isActionable: z.boolean().optional(),
  bikeModelName: z.string().nullable(),
  // BEL-813: SUM(quantity * unitCost * (1 - discount%)) over active material
  // lines. Null when the request has no priced lines (or is not a material request).
  materialTotal: z.number().nullable().optional(),
});

export type RequestHydrated = z.infer<typeof RequestHydrated>;

/**
 * @description Canonical request-creation body — shared by the API route boundary
 * (@hono/zod-openapi) and the webapp mutation input. Single source of truth for
 * BEL-446 (previously triplicated across api/webapp/contracts).
 */
export const CreateRequestBodySchema = z.object({
  documentTypeId: DocumentTypeId,
  payload: z.record(z.string(), z.unknown()),
  priority: RequestPriority.optional(),
  createdBy: EntityId,
  locationId: LocationId.nullable().optional(),
  bikeId: BikeId.nullable().optional(),
  bikeIds: z.array(BikeId).optional(),
});
export type CreateRequestBodySchema = z.infer<typeof CreateRequestBodySchema>;

/**
 * @description One entry in the audit timeline for a request (from fsm_transition_logs).
 */
export const TransitionLogEntryDto = z.object({
  id: z.string().uuid(),
  documentId: DocumentId,
  transitionId: TransitionId,
  transitionName: z.string(),
  transitionTranslations: FsmTranslationsSchema.nullable(),
  fromStateId: StateId,
  fromStateName: z.string(),
  fromStateTranslations: FsmTranslationsSchema.nullable(),
  toStateId: StateId,
  toStateName: z.string(),
  toStateTranslations: FsmTranslationsSchema.nullable(),
  executedByEntityId: EntityId.nullable(),
  executedByName: z.string().nullable(),
  executedAt: z.string(),
  fieldValues: z.record(z.string(), z.unknown()).default({}),
});

export type TransitionLogEntryDto = z.infer<typeof TransitionLogEntryDto>;
